import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { Layout } from './components/layout/Layout';
import { InboxPage } from './components/inbox/InboxPage';
import { CampaignDashboard } from './components/campaigns/CampaignDashboard';
import { ContactsPage } from './components/contacts/ContactsPage';
import { DomainsPage } from './components/domains/DomainsPage';
import { WarmupPage } from './components/warmup/WarmupPage';
import { BulkSendPage } from './components/bulk-send/BulkSendPage';
import { InternalSendPage } from './components/internal-send/InternalSendPage';
import { LoginPage } from './components/auth/LoginPage';
import { EmailVerificationPage } from './components/bulk-send/EmailVerificationPage';
import { LOCAL_VERIFICATION_AVAILABLE } from './components/bulk-send/emailVerification';
import { clearAuthSession } from './utils/authSession';
import type { DomainConfig, SenderAccount } from './components/layout/AccountSwitcher';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';
const LOCAL_SAFE_PREVIEW = process.env.NODE_ENV === 'development'
  && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
  && new URLSearchParams(window.location.search).get('safe-preview') === '1';

// ─── Auth type ─────────────────────────────────────────────
interface AuthInfo {
  token: string;
  role: string;
  regions: string[];  // countryCode values this admin can see
  label: string;
}

// ─── Regional Domain Config ────────────────────────────────
// 6 domains × Spacemail SMTP
const STATIC_ACCOUNTS: DomainConfig[] = [
  // ═══ lawhelper.lat — Latin America ═══
  {
    countryCode: 'LAT',
    country: 'Latin America',
    domain: 'lawhelper.lat',
    senders: [
      { email: 'leo@lawhelper.lat', domain: 'lawhelper.lat', displayName: 'Leo | Lawhelper', dailySendLimit: 50, sentToday: 0 },
      { email: 'loo@lawhelper.lat', domain: 'lawhelper.lat', displayName: 'Loo | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
  // ═══ lawhelper.us — North America ═══
  {
    countryCode: 'US',
    country: 'North America',
    domain: 'lawhelper.us',
    senders: [
      { email: 'alex@lawhelper.us', domain: 'lawhelper.us', displayName: 'Alex | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
  // ═══ lawhelper.eu — Europe ═══
  {
    countryCode: 'EU',
    country: 'Europe',
    domain: 'lawhelper.eu',
    senders: [
      { email: 'leo@lawhelper.eu', domain: 'lawhelper.eu', displayName: 'Leo | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
  // ═══ lawhelper.asia — Asia-Pacific ═══
  {
    countryCode: 'ASIA',
    country: 'Asia-Pacific',
    domain: 'lawhelper.asia',
    senders: [
      { email: 'alex@lawhelper.asia', domain: 'lawhelper.asia', displayName: 'Alex | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
  // ═══ lawhelper.ae — Middle East ═══
  {
    countryCode: 'AE',
    country: 'Middle East',
    domain: 'lawhelper.ae',
    senders: [
      { email: 'alex@lawhelper.ae', domain: 'lawhelper.ae', displayName: 'Alex | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
  // ═══ lawhelper.africa — Africa ═══
  {
    countryCode: 'AF',
    country: 'Africa',
    domain: 'lawhelper.africa',
    senders: [
      { email: 'alex@lawhelper.africa', domain: 'lawhelper.africa', displayName: 'Alex | Lawhelper', dailySendLimit: 50, sentToday: 0 },
    ],
  },
];

export default function App() {
  if (LOCAL_VERIFICATION_AVAILABLE && window.location.pathname === '/email-validation') return <EmailVerificationPage />;
  return <AcquisitionApp />;
}

function AcquisitionApp() {
  const [auth, setAuth] = useState<AuthInfo | null>(() => LOCAL_SAFE_PREVIEW ? {
    token: 'local-safe-preview',
    role: 'preview',
    regions: ['LAT', 'US', 'EU', 'ASIA', 'AE', 'AF'],
    label: 'Local safe preview',
  } : null);

  // Filter accounts based on region access
  const accounts = auth
    ? STATIC_ACCOUNTS.filter((a) => auth.regions.map(r => r.toLowerCase()).includes(a.countryCode.toLowerCase()))
    : [];

  const [selectedAccount, setSelectedAccount] = useState<SenderAccount | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);

  // Auto-select first account when auth changes
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0].senders[0]);
    }
  }, [accounts, selectedAccount]);

  const pollUnread = useCallback(async () => {
    if (!auth || LOCAL_SAFE_PREVIEW) return;
    try {
      const res = await fetch(`${API}/replies/unread`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const data = await res.json();
      setUnreadCount((data.threads || []).length);
    } catch { }
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    pollUnread();
    const t = setInterval(pollUnread, 30000);
    return () => clearInterval(t);
  }, [auth, pollUnread]);

  function handleLogin(authInfo: AuthInfo) {
    setAuth(authInfo);
    setSelectedAccount(null); // will auto-select via useEffect
  }

  function handleLogout() {
    setAuth(null);
    setSelectedAccount(null);
    clearAuthSession(API);
  }

  // ─── Not logged in → show login page ──────────────────────
  if (!auth) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // ─── Logged in → show dashboard ───────────────────────────
  if (!selectedAccount) return null; // waiting for auto-select

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <Layout
            accounts={accounts}
            selectedAccount={selectedAccount}
            onSelectAccount={setSelectedAccount}
            unreadCount={unreadCount}
            composeOpen={composeOpen}
            onComposeOpen={() => setComposeOpen(true)}
            onComposeClose={() => setComposeOpen(false)}
            authLabel={auth.label}
            onLogout={handleLogout}
            previewMode={LOCAL_SAFE_PREVIEW}
          />
        }>
          <Route index element={<Navigate to="/inbox" replace />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="campaigns" element={<CampaignDashboard />} />
          <Route path="bulk-send" element={<BulkSendPage previewMode={LOCAL_SAFE_PREVIEW} />} />
          <Route path="internal-send" element={<InternalSendPage previewMode={LOCAL_SAFE_PREVIEW} />} />
          <Route path="contacts" element={<ContactsPage accounts={accounts} />} />
          <Route path="domains" element={<DomainsPage accounts={accounts} />} />
          <Route path="warmup" element={<WarmupPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
